import { useId, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { keepDecimal } from '../lib/number'
import { isUnreadableFigure, type TableColumn } from '../lib/tableView'
import type { TableView } from '../lib/useTableView'

/**
 * A column heading that sorts its table (TRKC-110). The heading holds a button, as the APG's
 * sortable table does: pressing it sorts ascending, then descending, then back to the order
 * the rows came in, and the heading says which with `aria-sort` — on the sorted column only.
 */
export function SortableHeader<Row>({
  view,
  column,
  className,
  children,
}: {
  view: TableView<Row>
  column: string
  className?: string
  children: ReactNode
}) {
  const direction = view.directionOf(column)

  return (
    <th scope="col" className={className} aria-sort={direction ?? undefined}>
      <button type="button" className="table__sort" onClick={() => view.toggleSort(column)}>
        {children}
        {/* The arrow is drawn for the eye; aria-sort already says it to a screen reader. */}
        <span className={direction === null ? 'table__sort-mark table__sort-mark--idle' : 'table__sort-mark'} aria-hidden="true">
          {direction === 'ascending' ? '▲' : direction === 'descending' ? '▼' : '↕'}
        </span>
      </button>
    </th>
  )
}

/**
 * A column's filter box, in the row of filters under the headings. What it narrows is the
 * column's own rule (`TableColumn.filter`), so the box and the rule cannot disagree about
 * the kind of filter: words, a choice from a list, a least or most figure, or a first day.
 * A column with no filter gets an empty cell.
 */
export function FilterCell<Row>({
  view,
  column,
  label,
  options = [],
  disabled = false,
}: {
  view: TableView<Row>
  column: TableColumn<Row>
  /** The column's heading, as the box's name says it. */
  label: string
  /** The choices a list filter offers, after "Any". */
  options?: readonly { value: string; label: string }[]
  /** The page cannot take a new filter right now — a row is being corrected. */
  disabled?: boolean
}) {
  const { t } = useTranslation()
  // Two tables on one page can have a column of the same name: the carrier's three fleet tables.
  const complaintId = useId()
  const filter = column.filter

  if (filter === undefined) {
    return <td className="table__filter" />
  }

  const value = view.filterOf(column.key)
  const set = (next: string) => view.setFilter(column.key, next)
  // The column's name over its box, shown only where the box no longer sits under its heading
  // — a table drawn as cards on a phone (the agents). The box carries the name for assistive
  // tech already, so this one is for the eye alone.
  const named = (
    <span className="table__filter-label" aria-hidden="true">
      {label}
    </span>
  )

  switch (filter.kind) {
    case 'text':
      return (
        <td className="table__filter">
          {named}
          <input
            type="search"
            value={value}
            disabled={disabled}
            onChange={(event) => set(event.target.value)}
            aria-label={t('table.filterLabel', { column: label })}
            placeholder={t('table.filterPlaceholder')}
          />
        </td>
      )
    case 'choice':
      return (
        <td className="table__filter">
          {named}
          <select
            value={value}
            disabled={disabled}
            onChange={(event) => set(event.target.value)}
            aria-label={t('table.filterLabel', { column: label })}
          >
            <option value="">{t('table.any')}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </td>
      )
    case 'atLeast':
    case 'atMost': {
      const unreadable = isUnreadableFigure(value)
      return (
        <td className="table__filter">
          {named}
          <input
            inputMode="decimal"
            value={value}
            disabled={disabled}
            onChange={(event) => set(keepDecimal(value, event.target.value))}
            aria-label={t(filter.kind === 'atLeast' ? 'table.atLeastLabel' : 'table.atMostLabel', { column: label })}
            placeholder={t(filter.kind === 'atLeast' ? 'table.atLeastPlaceholder' : 'table.atMostPlaceholder')}
            aria-invalid={unreadable || undefined}
            aria-describedby={unreadable ? complaintId : undefined}
          />
          {unreadable && (
            <span id={complaintId} className="table__filter-error">
              {t('table.notANumber')}
            </span>
          )}
        </td>
      )
    }
    case 'onOrAfter':
      return (
        <td className="table__filter">
          {named}
          <input
            type="date"
            value={value}
            disabled={disabled}
            onChange={(event) => set(event.target.value)}
            aria-label={t('table.onOrAfterLabel', { column: label })}
          />
        </td>
      )
  }
}

/**
 * How many rows the filters keep, and the way back to all of them — said only while a filter
 * narrows the table, so a short list is never mistaken for the whole one.
 */
export function FilterSummary<Row>({ view }: { view: TableView<Row> }) {
  const { t } = useTranslation()

  return (
    <div className="table__filter-summary" aria-live="polite">
      {view.active > 0 && (
        <p className="field__hint">
          {t('table.showing', { shown: view.rows.length, total: view.total })}{' '}
          <button type="button" className="button button--quiet" onClick={view.clearFilters}>
            {t('table.clearFilters')}
          </button>
        </p>
      )}
    </div>
  )
}
